import { useState } from "react";
import type { FastbootService, FastbootVariables } from "../../core/fastboot/FastbootService";
import { DeviceInfoTable } from "../../components/DeviceInfoTable";
import { FilePicker } from "../../components/FilePicker";
import { ProgressPanel } from "../../components/ProgressPanel";
import { hashFile } from "../../core/files/ChunkedFileReader";
import { operationLogger } from "../../core/operations/OperationLogger";

export function FastbootPage({ service, connected }: { service: FastbootService; connected: boolean }) {
  const [variables, setVariables] = useState<FastbootVariables>({});
  const [query, setQuery] = useState("product");
  const [partition, setPartition] = useState("boot");
  const [image, setImage] = useState<File>();
  const [sha, setSha] = useState("");
  const [progress, setProgress] = useState<{ phase: string; completedBytes: number; totalBytes: number; startedAt: number }>();
  const [busy, setBusy] = useState(false);
  const [advanced, setAdvanced] = useState("getvar:product");

  const run = async (operation: string, task: () => Promise<void>) => {
    setBusy(true);
    const started = operationLogger.start("FASTBOOT", operation);
    try {
      await task();
      operationLogger.success("FASTBOOT", operation, started, "Thiết bị đã trả về OKAY.");
    } catch (error) {
      operationLogger.failure("FASTBOOT", operation, started, error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };

  const progressCallback = (value: { phase: string; completedBytes: number; totalBytes?: number; startedAt: number }) => {
    setProgress({ phase: value.phase, completedBytes: value.completedBytes, totalBytes: value.totalBytes ?? image?.size ?? 0, startedAt: value.startedAt });
  };
  const loadVars = () => void run("getvar all", async () => setVariables(await service.getvarAll()));
  const hash = async (file?: File) => { if (file) setSha(await hashFile(file)); };
  const unlock = (label: string, command: string) => {
    if (!window.confirm(`${label} sẽ xóa dữ liệu trên thiết bị và có thể làm mất trạng thái bảo hành.\n\nLệnh: ${command}\n\nChỉ tiếp tục nếu bạn đã sao lưu và chấp nhận rủi ro.`)) return;
    void run(label, () => service.command(command).then(() => undefined));
  };
  const wipeData = () => {
    if (!window.confirm("fastboot -w sẽ xóa userdata và cache trên thiết bị, tương đương Wipe Data / Factory Reset.\n\nChỉ tiếp tục nếu bạn đã sao lưu dữ liệu và chấp nhận không thể hoàn tác.")) return;
    void run("fastboot -w", () => service.wipeData());
  };

  return (
    <>
      <div className="feature-layout fastboot-layout">
      <section className="panel fastboot-device-panel">
        <div className="panel-heading">
          <div><p className="eyebrow">FASTBOOT / FASTBOOTD</p><h2>Biến thiết bị</h2></div>
          <button className="secondary" disabled={!connected || busy} onClick={loadVars}>Đọc getvar all</button>
        </div>
        <DeviceInfoTable values={{
          VID: service.transport.usbDevice?.vendorId ? `0x${service.transport.usbDevice.vendorId.toString(16)}` : undefined,
          PID: service.transport.usbDevice?.productId ? `0x${service.transport.usbDevice.productId.toString(16)}` : undefined,
          product: variables.product,
          serialno: variables.serialno,
          "version-bootloader": variables["version-bootloader"],
          "current-slot": variables["current-slot"],
          "slot-count": variables["slot-count"],
          "is-userspace": variables["is-userspace"],
          "max-download-size": variables["max-download-size"],
          unlocked: variables.unlocked,
          secure: variables.secure,
        }} />
        <div className="inline-form">
          <input className="text-input" value={query} onChange={(event) => setQuery(event.target.value)} />
          <button className="secondary" disabled={!connected || busy || !query.trim()} onClick={() => void run(`getvar ${query}`, async () => {
            const responses = await service.getvar(query);
            setVariables((value) => ({ ...value, [query]: responses.at(-1)?.payload ?? "" }));
          })}>getvar</button>
        </div>
        <div className="quick-actions">
          <button onClick={() => void run("reboot", () => service.reboot().then(() => undefined))} disabled={!connected || busy}>Reboot system</button>
          <button onClick={() => void run("reboot bootloader", () => service.reboot("bootloader").then(() => undefined))} disabled={!connected || busy}>Bootloader</button>
          <button onClick={() => void run("reboot recovery", () => service.reboot("recovery").then(() => undefined))} disabled={!connected || busy}>Recovery</button>
          <button onClick={() => void run("reboot fastbootd", () => service.reboot("fastboot").then(() => undefined))} disabled={!connected || busy}>Fastbootd</button>
          <button onClick={() => void run("set active slot", () => service.setActive("a").then(() => undefined))} disabled={!connected || busy}>Set slot A</button>
          <button onClick={() => void run("set active slot", () => service.setActive("b").then(() => undefined))} disabled={!connected || busy}>Set slot B</button>
        </div>
        <div className="unlock-panel">
          <div className="unlock-panel-heading"><strong>Unlock bootloader</strong><span>RỦI RO CAO · thường xóa userdata</span></div>
          <p className="muted small">Các lệnh chỉ được gửi khi bạn xác nhận. Thiết bị có thể yêu cầu xác nhận thêm trên màn hình bootloader hoặc từ chối do chính sách nhà sản xuất.</p>
          <div className="quick-actions">
            <button className="danger-button" onClick={() => unlock("flashing unlock", "flashing unlock")} disabled={!connected || busy}>Unlock</button>
            <button className="danger-button" onClick={() => unlock("flashing unlock_critical", "flashing unlock_critical")} disabled={!connected || busy}>Unlock critical</button>
            <button className="danger-button" onClick={() => unlock("oem unlock", "oem unlock")} disabled={!connected || busy}>OEM unlock</button>
          </div>
        </div>
      </section>

      <section className="panel fastboot-image-panel">
        <div className="panel-heading">
          <div><p className="eyebrow">IMAGE OPERATIONS</p><h2>Boot hoặc flash image</h2></div>
          <span className="muted">Không tự bỏ qua AVB / secure boot</span>
        </div>
        <FilePicker label="Image .img" accept=".img,application/octet-stream" file={image} onChange={(file) => { setImage(file); setSha(""); void hash(file); }} />
        <div className="image-meta">
          <span>Partition <b>{partition}</b></span>
          <span>Kích thước <b>{image ? `${image.size.toLocaleString("vi-VN")} bytes` : "—"}</b></span>
          <span>SHA-256 <code>{sha || "Đang chờ image"}</code></span>
        </div>
        <input className="text-input" value={partition} onChange={(event) => setPartition(event.target.value.replace(/[^a-zA-Z0-9_.-]/g, ""))} placeholder="Tên partition" />
        <div className="button-row">
          <button className="primary" disabled={!connected || !image || busy} onClick={() => void run(`flash ${partition}`, () => service.flash(partition, image!, progressCallback).then(() => setProgress(undefined)))}>Flash vào partition</button>
          <button className="secondary" disabled={!connected || !image || busy} onClick={() => void run("boot image", () => service.boot(image!, progressCallback).then(() => setProgress(undefined)))}>Boot tạm thời</button>
        </div>
        <ProgressPanel progress={progress} onCancel={() => void service.cancelCurrentOperation()} />
        <p className="muted small">Flash image lớn tự động áp dụng cơ chế tương đương <code>-S 128M</code>: raw/sparse image được chia thành nhiều sparse payload rồi ghi tuần tự cùng partition. Boot tạm thời không ghi vĩnh viễn và không tự chia image.</p>
      </section>

      <section className="panel fastboot-erase-panel">
        <div className="panel-heading"><div><p className="eyebrow">PARTITION CONTROL</p><h2>Erase</h2></div></div>
        <button className="danger-button" disabled={!connected || busy} onClick={wipeData}>fastboot -w · Wipe data</button>
        <p className="muted small">Xóa userdata và cache nếu thiết bị có partition cache. Thao tác không thể hoàn tác.</p>
        <div className="inline-form">
          <input className="text-input" value={partition} onChange={(event) => setPartition(event.target.value.replace(/[^a-zA-Z0-9_.-]/g, ""))} />
          <button className="danger-button" disabled={!connected || busy || !partition} onClick={() => void run(`erase ${partition}`, () => service.erase(partition).then(() => undefined))}>Erase partition</button>
        </div>
        <p className="muted small">Erase có thể làm mất hệ điều hành và không thể hoàn tác; hãy kiểm tra partition trước khi gửi.</p>
      </section>

      <section className="panel fastboot-advanced-panel">
        <div className="panel-heading"><div><p className="eyebrow">ADVANCED</p><h2>Lệnh Fastboot tùy chỉnh</h2></div></div>
        <input className="text-input" value={advanced} onChange={(event) => setAdvanced(event.target.value)} />
        <button className="secondary" disabled={!connected || busy || !advanced.trim()} onClick={() => void run("advanced command", () => service.command(advanced).then(() => undefined))}>Gửi lệnh</button>
        <p className="muted small">Lệnh được gửi trực tiếp sau khi bấm nút; hãy kiểm tra chính xác cú pháp và hậu quả. Phản hồi INFO/OKAY/FAIL vẫn được ghi vào log.</p>
      </section>
      </div>
    </>
  );
}
